import React, { useState, useEffect } from 'react';
import ApprovalModal from './components/ApprovalModal';
import RejectReasonModal from './components/RejectReasonModal';
import './SubmissionReviews.css';

/**
 * Submission Reviews Tab Component
 * Displays pending testimonials and form submissions for review
 */
function SubmissionReviewsTab({ apiBase, roleColor, onReviewChange }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    loadSubmissions();
  }, [apiBase, filter]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const url = new URL(`${apiBase}/approver/pending-submissions`);
      url.searchParams.append('status', filter === 'all' ? '' : filter);
      
      const response = await fetch(url, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();
      if (data.ok) {
        setSubmissions(Array.isArray(data.data) ? data.data : []);
      }
    } catch (err) {
      console.error('Error loading submissions:', err);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (item) => {
    setSelectedItem(item);
    setShowApprovalModal(true);
  };

  const handleReject = (item) => {
    setSelectedItem(item);
    setShowRejectModal(true);
  };

  const handleApprovalSubmit = async (reason) => {
    if (!selectedItem) return;

    try {
      const response = await fetch(`${apiBase}/approver/submission/${selectedItem.id}/approve`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        setShowApprovalModal(false);
        setSelectedItem(null);
        onReviewChange();
        loadSubmissions();
      } else {
        alert('Error approving submission');
      }
    } catch (err) {
      console.error('Error approving submission:', err);
      alert('Error approving submission');
    }
  };

  const handleRejectSubmit = async (reason) => {
    if (!selectedItem) return;

    try {
      const response = await fetch(`${apiBase}/approver/submission/${selectedItem.id}/reject`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        setShowRejectModal(false);
        setSelectedItem(null);
        onReviewChange();
        loadSubmissions();
      } else {
        alert('Error rejecting submission');
      }
    } catch (err) {
      console.error('Error rejecting submission:', err);
      alert('Error rejecting submission');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#f59e0b';
      case 'approved':
        return '#10b981';
      case 'rejected':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, length = 100) => {
    if (!text) return '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  return (
    <div className="submission-reviews-tab">
      <div className="tab-header">
        <h2>Review Pengajuan</h2>
        <div className="filter-group">
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
            style={filter === 'pending' ? { borderBottomColor: roleColor } : {}}
          >
            Menunggu
          </button>
          <button
            className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
            style={filter === 'approved' ? { borderBottomColor: roleColor } : {}}
          >
            Disetujui
          </button>
          <button
            className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
            style={filter === 'rejected' ? { borderBottomColor: roleColor } : {}}
          >
            Ditolak
          </button>
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
            style={filter === 'all' ? { borderBottomColor: roleColor } : {}}
          >
            Semua
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading submissions...</div>
      ) : submissions.length > 0 ? (
        <div className="submissions-grid">
          {submissions.map(item => (
            <div key={item.id} className="submission-card">
              <div className="card-header">
                <h3>{item.submitter_name || 'Anonymous'}</h3>
                <span 
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(item.status) }}
                >
                  {item.status}
                </span>
              </div>

              <div className="card-body">
                <div className="submission-type">
                  <span className="type-label">{item.type || 'Testimonial'}</span>
                </div>

                <div className="submission-content">
                  <p>{truncateText(item.content || item.message, 150)}</p>
                </div>

                <div className="card-meta">
                  <span className="meta-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-13c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3c.55 0 1-.45 1-1s-.45-1-1-1h-2V8c0-.55-.45-1-1-1z" />
                    </svg>
                    {formatDate(item.created_at || item.submitted_at)}
                  </span>
                  {item.rating && (
                    <span className="meta-item rating">
                      <span className="stars">{'⭐'.repeat(item.rating)}</span>
                      {item.rating}/5
                    </span>
                  )}
                </div>
              </div>

              <div className="card-footer">
                {item.status === 'pending' && (
                  <>
                    <button
                      className="btn btn-approve"
                      onClick={() => handleApprove(item)}
                      style={{ color: roleColor }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Setujui
                    </button>
                    <button
                      className="btn btn-reject"
                      onClick={() => handleReject(item)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      Tolak
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>Tidak ada pengajuan untuk ditinjau</p>
        </div>
      )}

      {showApprovalModal && (
        <ApprovalModal
          item={selectedItem}
          roleColor={roleColor}
          onSubmit={handleApprovalSubmit}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedItem(null);
          }}
          type="submission"
        />
      )}

      {showRejectModal && (
        <RejectReasonModal
          item={selectedItem}
          roleColor={roleColor}
          onSubmit={handleRejectSubmit}
          onClose={() => {
            setShowRejectModal(false);
            setSelectedItem(null);
          }}
          type="submission"
        />
      )}
    </div>
  );
}

export default SubmissionReviewsTab;
